import { Suspense, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Logo } from '@proaxiv/brands'
import { useAuth } from '../auth/AuthProvider'
import { useCapabilities } from '../auth/useCapabilities'
import { OrgSwitcher } from './OrgSwitcher'
import { LanguageSwitcher } from './LanguageSwitcher'
import { NotificationBell } from './NotificationBell'

const UserIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
)

const ChevronDown = ({ open }: { open: boolean }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    aria-hidden="true"
    className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
  >
    <path d="M2 4l4 4 4-4" />
  </svg>
)

const CrownIcon = ({ size = 13 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 20h20M5 20l-1-9 5.5 5L12 7l2.5 9L20 11l-1 9" />
  </svg>
)

interface Props {
  sidebarOpen: boolean
}

/** Top navigation bar with brand logo, user dropdown, Admin badge, and notification bell. */
export function Header({ sidebarOpen }: Props) {
  const { t } = useTranslation()
  const { username, logout } = useAuth()
  const { isAdmin } = useCapabilities()
  const [userOpen, setUserOpen] = useState(false)
  const userMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <header
      className={[
        // `transform-gpu` keeps this on its own compositor layer so mobile Chrome/Safari
        // doesn't visibly drift it during the address-bar hide/show scroll animation (see
        // AppLayout's decorative background for the same fix/rationale).
        'fixed top-0 z-40 flex h-16 items-center justify-between bg-white px-4 md:px-6',
        'border-pax-purple/25 transform-gpu border-b transition-[left] duration-200',
        // Mobile: always clear the persistent icon rail (burger lives in the rail, not here).
        // Desktop: offset right of the sidebar rail.
        'right-0 left-11',
        sidebarOpen ? 'md:left-[260px]' : 'md:left-[60px]',
      ].join(' ')}
    >
      {/* Left: logo + language switcher (burger/open-close is in the sidebar rail) */}
      <div className="flex items-center gap-3">
        {/* Logo — smaller on mobile to keep the tight header row uncluttered, full size from md up */}
        <div className="h-6 md:h-9">
          <Logo height={36} className="!h-full w-auto object-contain" />
        </div>

        <Suspense fallback={null}>
          <LanguageSwitcher />
        </Suspense>
      </div>

      {/* Right controls */}
      <div className="flex items-center gap-2 md:gap-3">
        {/* Organization switcher — desktop only from md up. On mobile the side drawer's
            "Your organizations" list is the only org switcher, so it doesn't need to be
            duplicated in the already-tight topbar row. */}
        <div className="hidden md:block">
          <OrgSwitcher />
        </div>

        {/* User dropdown — desktop only from md up. On mobile the email and sign-out action
            live in the side drawer instead (see Sidebar's mobile-only account footer), since
            there isn't enough room in the topbar row to show the email there too. */}
        <div className="relative hidden md:block" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setUserOpen((o) => !o)}
            className="hover:bg-pax-light-purple text-pax-black flex cursor-pointer items-center gap-2 rounded-xl px-2 py-1.5 text-sm font-medium transition-colors focus-visible:outline-none md:px-3"
            aria-haspopup="true"
            aria-expanded={userOpen}
          >
            <span className="bg-pax-light-purple text-pax-blue flex h-7 w-7 items-center justify-center rounded-full">
              <UserIcon />
            </span>
            {/* Username hidden on small screens to save space */}
            <span className="hidden sm:inline">{username}</span>
            <ChevronDown open={userOpen} />
          </button>

          {userOpen && (
            <ul
              className="border-pax-purple/30 absolute top-full right-0 z-50 mt-1 w-44 overflow-hidden rounded-xl border bg-white p-1 shadow-lg"
              role="menu"
            >
              <li role="none">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setUserOpen(false)
                    logout()
                  }}
                  className="text-pax-red hover:bg-pax-red/5 w-full cursor-pointer rounded-lg px-3 py-2 text-left text-sm"
                >
                  {t('header_signout')}
                </button>
              </li>
            </ul>
          )}
        </div>

        {/* Admin badge — only shown for users with the admin realm role. Always shows the full
            icon + "Admin" label at every breakpoint (the user dropdown being desktop-only now
            frees up enough room in the mobile topbar row for this). */}
        {isAdmin && (
          <div
            className="bg-pax-orange-warm/10 text-pax-orange-warm flex items-center gap-1.5 rounded-xl px-2.5 py-1.5 text-sm font-medium md:px-3"
            aria-label="Admin"
            title="Admin"
          >
            <CrownIcon size={13} />
            <span>Admin</span>
          </div>
        )}

        <NotificationBell />
      </div>
    </header>
  )
}
